import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, UserPlus, Crown, ShieldCheck, User as UserIcon, Trash2, Lock } from "lucide-react";

const roleBadge = (role) => {
  if (role === "owner") return { icon: Crown, cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Owner" };
  if (role === "admin") return { icon: ShieldCheck, cls: "bg-accent/10 text-accent border-accent/20", label: "Admin" };
  return { icon: UserIcon, cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Member" };
};

export default function Team() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [inviting, setInviting] = useState(false);

  const load = useCallback(() => api.get("/team").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.trim()) { toast.error("Enter an email"); return; }
    setInviting(true);
    try {
      await api.post("/team/invite", { email, role });
      toast.success(`Invited ${email}`); setEmail(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not invite"); }
    finally { setInviting(false); }
  };

  const changeRole = async (m, newRole) => {
    try { await api.patch(`/team/${m.id}`, { role: newRole }); load(); }
    catch (e) { toast.error("Could not update role"); }
  };
  const removeMember = async (m) => {
    try { await api.delete(`/team/${m.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Could not remove"); }
  };

  if (!data) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="team-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight inline-flex items-center gap-2"><Users className="h-7 w-7" /> Team</h1>
        <p className="text-slate-500 mt-1">Invite teammates and assign roles. {data.can_manage && <>Using <span className="font-semibold text-slate-900">{data.seats_used}</span>{data.seat_limit < 9999 ? ` of ${data.seat_limit}` : ""} seats.</>}</p>
      </div>

      {!data.can_manage ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="team-upgrade-gate">
          <Lock className="h-10 w-10 mx-auto text-slate-300" />
          <h3 className="font-heading text-xl font-semibold mt-4">Team seats are a Pro &amp; Enterprise feature</h3>
          <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">Upgrade your plan to invite teammates, assign Admin/Member roles, and collaborate on bids.</p>
          <button onClick={() => navigate("/dashboard/billing")} data-testid="team-upgrade-button" className="mt-5 bg-emerald-900 text-white rounded-md px-5 py-2.5 text-sm font-medium hover:bg-emerald-800 transition-colors">View plans</button>
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <h3 className="font-heading text-lg font-semibold mb-4 inline-flex items-center gap-2"><UserPlus className="h-5 w-5" /> Invite a teammate</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <input data-testid="invite-email-input" type="email" placeholder="teammate@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40" />
              <select data-testid="invite-role-select" value={role} onChange={(e) => setRole(e.target.value)} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <button onClick={invite} disabled={inviting} data-testid="invite-button" className="bg-emerald-900 text-white rounded-md px-5 py-2 text-sm font-medium hover:bg-emerald-800 transition-colors disabled:opacity-60">{inviting ? "Inviting…" : "Send invite"}</button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100">
            {data.members.map((m) => {
              const b = roleBadge(m.role);
              return (
                <div key={m.id} className="flex items-center justify-between gap-4 p-4" data-testid={`team-member-${m.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-semibold shrink-0">{(m.name || m.email)[0]?.toUpperCase()}</div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{m.name || m.email}</p>
                      <p className="text-xs text-slate-400 truncate">{m.email} · {m.status}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {m.role === "owner" ? (
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${b.cls}`}><b.icon className="h-3.5 w-3.5" />{b.label}</span>
                    ) : (
                      <>
                        <select value={m.role} onChange={(e) => changeRole(m, e.target.value)} data-testid={`role-select-${m.id}`} className="text-xs rounded-md border border-slate-200 px-2 py-1">
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                        <button onClick={() => removeMember(m)} data-testid={`remove-member-${m.id}`} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 className="h-4 w-4" /></button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
